'use strict';
import { Injectable } from '@angular/core';
import { clone } from '@helpers/arrays';
import { SimulationSetValidationError } from '@interfaces/simulation-set-errors';
import { DatasetDetails } from '@models/dataset';
import { ResultsetItem } from '@models/resultset-item';
import { Scenario } from '@models/scenario';
import { OpenIdConnectService } from '@services/auth/open-id-connect.service';
import { ResultsStoreService } from '@services/results/results-store.service';
import { ResultsService } from '@services/results/results.service';
import { BehaviorSubject } from 'rxjs';
import { SimulationRunnerService } from './simulation-runner.service';
import { SimulationSetValidator } from './simulation-set-validator';

import { v4 as uuidv4 } from 'uuid';

@Injectable({ providedIn: 'root' })
export class SimulationSetService {
  private nextSimulationID: number;
  private forceFetch = true;
  private _simulationSetState: ResultsetItem[] = null;
  private _simulationSetStateBeforeEmpty: ResultsetItem[] = null;
  private _baselineList: Map<number, any>;
  private _datasetMap: Map<Number, any>;
  private _stressList: Map<number, any>;
  private _scenarioMap: Map<number, any>;
  private _DBstate: 'fetching' | 'fetched' | 'running' | 'completed';
  private _currentResultSet: any[];
  private _isValidSimulationSet: boolean;
  public listOfBaselinesUsed$ = new BehaviorSubject<number[]>([]);
  public listOfStressesUsed$ = new BehaviorSubject<{ [baseline: number]: number[] }>({});
  public buildingSimulationSetState: boolean;

  public datasetArray$ = new BehaviorSubject<DatasetDetails[]>([]);
  public baselineList$ = new BehaviorSubject<Scenario[]>([]);
  public stressList$ = new BehaviorSubject<Scenario[]>([]);
  public simulationSetState$ = new BehaviorSubject<ResultsetItem[]>([]);

  public validationErrorList: SimulationSetValidationError[] = [];

  public get isValidSimulationSet() {
    return this._isValidSimulationSet;
  }
  public set isValidSimulationSet(value) {
    this._isValidSimulationSet = value;
  }
  public get currentResultSet(): any[] {
    return this._currentResultSet;
  }
  public set currentResultSet(value: any[]) {
    this._currentResultSet = value;
  }

  public get DBstate(): 'fetching' | 'fetched' | 'running' | 'completed' {
    return this._DBstate;
  }
  public set DBstate(value: 'fetching' | 'fetched' | 'running' | 'completed') {
    this._DBstate = value;
  }

  public get scenarioMap(): Map<number, any> {
    return this._scenarioMap;
  }
  public set scenarioMap(value: Map<number, any>) {
    this._scenarioMap = value;
  }

  public get baselineList() {
    return this._baselineList;
  }
  public get baselineListArray() {
    return this.baselineList ? Array.from(this.baselineList.values()) : [];
  }

  public set baselineList(value) {
    this._baselineList = value;
  }
  public get stressList() {
    return this._stressList;
  }
  public get stressListArray() {
    return this.stressList ? Array.from(this.stressList.values()) : [];
  }

  public set stressList(value) {
    this._stressList = value;
  }
  public get datasetMap() {
    return this._datasetMap;
  }
  public get datasetListArray() {
    return this.datasetMap ? Array.from(this.datasetMap.values()) : [];
  }

  public set datasetMap(value) {
    this._datasetMap = value;
  }
  public set simulationSetState(value) {
    this._simulationSetState = value;
  }
  public get simulationSetState() {
    return this._simulationSetState;
  }
  public set simulationSetStateBeforeEmpty(value: ResultsetItem[]) {
    this._simulationSetStateBeforeEmpty = value;
  }
  public get simulationSetStateBeforeEmpty() {
    return <ResultsetItem[]>this._simulationSetStateBeforeEmpty;
  }

  constructor(
    private simRunner: SimulationRunnerService,
    private resultsStore: ResultsStoreService,
    private resultsService: ResultsService,
    private auth: OpenIdConnectService
  ) {
    this.DBstate = 'fetching';
    this.buildingSimulationSetState = false;
    this.isValidSimulationSet = true;
    this.nextSimulationID = 0;
  }

  public getNewUUID() {
    return uuidv4();
  }

  /**
   * Loads the contents of the database into the Simulation state variables
   * @param preserveState If false, when destroying the service, the SimulationState will be resetted
   */
  public loadDataFromDB(preserveState = true) {
    this.buildingSimulationSetState = true;
    this.resultsStore.loadDataFromDB(null, true, true).then(res => {
      this.datasetMap = this.resultsStore.datasetMap;
      this.datasetArray$.next(Array.from(this.datasetMap.values()));

      this.scenarioMap = this.resultsStore.scenarioMap;
      this.baselineList = new Map(Array.from(this.scenarioMap).filter(([k, v]) => v.is_stress === 0));
      this.baselineList$.next(Array.from(this.baselineList.values()));
      this.stressList = new Map(Array.from(this.scenarioMap).filter(([k, v]) => v.is_stress === 1));
      this.stressList$.next(Array.from(this.stressList.values()));

      // Bug fix 2965 - Part 1: Simulation configuration should not be lost after switching between tabs.
      this.preserveSimulationSetStateWhileSwitchingTabs(preserveState);
      this.validateSimulationSet();
      this.buildingSimulationSetState = false;
      return this.simulationSetState || [];
    });
  }

  /**
   * Manually force the reset of the simulation state overriding the preserveState parameter in the loadDataFromDB() call
   */getResultTree
  public resetSimulationState() {
    this.simulationSetStateBeforeEmpty = null;
    this.simulationSetState = [];
    this.forceFetch = true;
    this.simulationSetState$.next([]);
  }

  /**
   * Copies the loaded state into a local variable so it can be reused if preserveState is true
   * Then creates the SimulationSetState and updates the BehaviourSubject with the Simulation tree
   */
  private preserveSimulationSetStateWhileSwitchingTabs(preserveState) {
    // If preserveState is false, always fetch from DB
    // if preserveState is true:
    //  first check if we have a previous state, if not fetch from DB and store
    if (!preserveState || this.forceFetch) {
      this.simulationSetState = this.flatSimulationSet(this.processResultSets(this.resultsStore.getResultTree()));
    }
    this.simulationSetStateBeforeEmpty = clone(this.simulationSetState);
    this.simulationSetState$.next(this.getSimulationTree());
  }

  /**
   * Generates a recursive tree of type ResultsetItem[] { ..., StressList: ResultsetItem[]} that holds
   * the relationship between baselines and stresses. Used by frontend templates and the Simulation Runner
   */
  public getSimulationTree(): ResultsetItem[] {
    const tree = [];
    this.simulationSetState.forEach(resultset => {
      if (resultset && !resultset.Is_Stress) {
        resultset.StressList = this.simulationSetState.filter(rs => {
          return rs.Parent === resultset.id;
        });
        tree.push(resultset);
      }
    });
    return tree;
  }

  public processResultSets(resultsets = [], userID?: number) {
    const completeMap = resultsets
      .map(resultset => {
        if (!resultset.Hidden) {
          const mappedResultset: ResultsetItem = {
            ...resultset,
            StressList: this.processResultSets(resultset.StressList),
          };
          if (mappedResultset.Results) {
            delete mappedResultset.Results;
          }
          return mappedResultset;
        }
      })
      .filter(Boolean);

    this.DBstate = 'fetched';
    this.forceFetch = false;
    return completeMap;
  }

  /**
   * Deletes baseline id from the state and all its associated stresses, validates the state and pushes the behaviour subject forward
   * to update the attached templates
   */
  public removeBaselineFromState(id: number) {
    this.simulationSetState = this.simulationSetState.filter(rs => rs.id !== id && rs.Parent !== id);
    this.validateSimulationSet();
    this.simulationSetState$.next(this.getSimulationTree());
  }

  /**
   * Deletes stress id from the state, validates the state and pushes the behaviour subject forward
   * to update the attached templates
   */
  public removeStressFromState(id: number) {
    this.simulationSetState = this.simulationSetState.filter(rs => rs.id !== id);
    this.validateSimulationSet();
    this.simulationSetState$.next(this.getSimulationTree());
  }

  /**
   * Adds a new baseline to the state, validates the state and updates the Behaviour Subject with the new tree
   */
  public addNewBaselineToState(scenarioId: number) {
    const scenario: Scenario = this.baselineList.get(scenarioId);
    const dataset: DatasetDetails = this.datasetListArray && this.datasetListArray[0] ? this.datasetListArray[0] : null;

    const foundPreviousRun = this.findResultsetItemInDB(scenario.scenario_id, dataset.DataSetID);
    const runid = foundPreviousRun ? foundPreviousRun.RunID : uuidv4();
    this.nextSimulationID++;

    const newBaseline: ResultsetItem = {
      DataSetID: dataset ? dataset.DataSetID : null,
      DataSetName: dataset ? dataset.DataSetName : null,
      Hidden: false,
      IsTemp: true,
      Is_Stress: false,
      Mnemonic: foundPreviousRun ? foundPreviousRun.Mnemonic : `${scenario.mnemonic}`,
      ResultSetID: foundPreviousRun ? foundPreviousRun.ResultSetID : -1,
      ResultSetId_Used: foundPreviousRun ? foundPreviousRun.ResultSetId_Used : -1,
      Description: foundPreviousRun ? foundPreviousRun.Description : scenario.scenario_description,
      Scenario_ID: scenario.scenario_id,
      Scenario_Mnemonic: scenario.mnemonic,
      IgnoreErrors: scenario.IgnoreErrors,
      TempResultSetID: foundPreviousRun ? foundPreviousRun.TempResultSetID : -1,
      RunID: runid,
      id: this.nextSimulationID,
      StressList: [],
      Parent: null,
      RunID_Used: null, // always empty if baseline
      Order: foundPreviousRun ? foundPreviousRun.Order : this.nextSimulationID,
    };
    this.simulationSetState.push(newBaseline);
    this.validateSimulationSet();
    this.simulationSetState$.next(this.getSimulationTree());
  }

  /**
   * On dataset change, cascade the new dataset used to all those stresses which had the same dataset id
   * as de baseline. If the stress dataset was different, let the stress unchanged
   * Then change the RunID_Used for stresses to the new baseline RunID
   * If then the stress is a new one or has already been used in this simulation, make sure to assign a new RunID
   */
  public cascadeDatasetChangeFromBaseline(baseline: ResultsetItem, previousDataset: number) {
    if (baseline.StressList) {
      baseline.StressList.forEach(stress => {
        if (stress.DataSetID === previousDataset || this.isDatasetInactiveValidator(stress)) {
          stress.DataSetID = baseline.DataSetID;
          stress.DataSetName = baseline.DataSetName;

          stress.RunID_Used = baseline.RunID;
          const mayexist = this.findResultsetItemInDB(baseline.Scenario_ID, stress.DataSetID, stress.Scenario_ID, stress.DataSetID);
          if (mayexist) {
            stress.RunID = mayexist.RunID;
            stress.Mnemonic = mayexist.Mnemonic;
          } else {
            stress.Mnemonic = stress.Scenario_Mnemonic;
          }
        }
      });
    }
    this.validateSimulationSet();
    this.simulationSetState$.next(this.getSimulationTree());
  }

  /**
   * On scenario change, cascade the new scenario used to all those stresses to get the
   * mnemonic updated if it hasn't been changed before
   */
  public cascadeScenarioMnemonicChangeFromBaseline(baseline: ResultsetItem) {
    if (baseline.StressList) {
      baseline.StressList.forEach(stress => {
        stress.RunID_Used = baseline.RunID;
        const mayexist = this.findResultsetItemInDB(baseline.Scenario_ID, stress.DataSetID, stress.Scenario_ID, stress.DataSetID);
        if (mayexist) {
          stress.RunID = mayexist.RunID;
          stress.Mnemonic = mayexist.Mnemonic;
        } else {
          stress.Mnemonic = stress.Scenario_Mnemonic;
        }
      });
    }
    this.validateSimulationSet();
    this.simulationSetState$.next(this.getSimulationTree());
  }

  /**
   * Adds a new stress to the state, validates the state and updates the Behaviour Subject with the new tree
   */
  public addNewStressToBaseline(scenarioId: number, parent: ResultsetItem) {
    const scenario = this.stressList.get(scenarioId);

    const foundPreviousRun = this.findResultsetItemInDB(parent.Scenario_ID, parent.DataSetID, scenario.scenario_id, parent.DataSetID);
    const runid = foundPreviousRun ? foundPreviousRun.RunID : uuidv4();
    this.nextSimulationID++;

    const newStress: ResultsetItem = {
      DataSetID: parent.DataSetID,
      DataSetName: parent.DataSetName,
      Hidden: false,
      IsTemp: true,
      Is_Stress: true,
      Mnemonic: foundPreviousRun ? foundPreviousRun.Mnemonic : `${scenario.mnemonic}`,
      ResultSetID: foundPreviousRun ? foundPreviousRun.ResultSetID : -1,
      ResultSetId_Used: parent.ResultSetID || -1,
      Description: foundPreviousRun ? foundPreviousRun.Description : scenario.scenario_description,
      Scenario_ID: scenario.scenario_id,
      Scenario_Mnemonic: scenario.mnemonic,
      IgnoreErrors: scenario.IgnoreErrors,
      TempResultSetID: foundPreviousRun ? foundPreviousRun.TempResultSetID : -1,
      RunID: runid,
      Parent: parent.id,
      id: this.nextSimulationID,
      RunID_Used: parent.RunID,
      Order: foundPreviousRun ? foundPreviousRun.Order : this.nextSimulationID,
    };
    this.simulationSetState.push(newStress);

    this.validateSimulationSet();
    this.simulationSetState$.next(this.getSimulationTree());
  }

  /**
   * Apply validation rules to the simulation set. This method is tied to the RUN button and enables/disables it when ran
   */
  public validateSimulationSet(): boolean {
    this.isValidSimulationSet = this.applyValidationRules();
    return this.isValidSimulationSet;
  }

  /**
   * Validation rules for the Simulation Set
   */
  private applyValidationRules(): boolean {
    let isValid = true;

    const identifiers = this.resultsService.resultSetIdentifiers$.getValue();

    this.simulationSetState.forEach(rs => {
      const validator = new SimulationSetValidator(rs, this.simulationSetState, this.datasetMap, identifiers)
        .validateCharactersInMnemonic()
        .validateDuplicateMnemonic()
        .validateMnemonicInDB()
        .validateDataset();
      rs.ValidationError = validator.resultset.ValidationError;
    });

    isValid = this.simulationSetState.reduce((accumulator, current) => {
      return accumulator && !Boolean(current.ValidationError);
    }, true);
    return isValid;
  }

  /**
   * Tests if the resultset dataset id is active in the system
   */
  private isDatasetInactiveValidator(resultset: ResultsetItem) {
    return !this.datasetMap.has(resultset.DataSetID);
  }

  /**
   * Flattens a simulation set with tree-shape (baseline[].StressList[]) into an array of ResultSetItem[]
   */
  public flatSimulationSet(simulationSet: ResultsetItem[]): ResultsetItem[] {
    const flatSet: ResultsetItem[] = [];
    if (simulationSet) {
      simulationSet.forEach(base => {
        const baseID = this.nextSimulationID;
        this.nextSimulationID++;
        flatSet.push({ ...base, id: baseID });
        if (base.StressList) {
          base.StressList.forEach(stress => {
            const stressID = this.nextSimulationID;
            this.nextSimulationID++;
            flatSet.push({ ...stress, id: stressID, Parent: baseID });
          });
        }
      });
    }
    return flatSet;
  }

  public canBeReordered(groupId: string, currentSimulationSet: ResultsetItem[]): boolean {
    let result = true;

    for (var prop in this.resultsStore.currentOrdering[groupId]) {
      if (this.resultsStore.currentOrdering[groupId].hasOwnProperty(prop)) {
        if (!currentSimulationSet.some(set => set.RunID === prop)) {
          result = false;
        }
      }
    }
    return result;
  }

  private getCurrentOrderOrNew(identifiers: ResultsetItem[], resultset: ResultsetItem, offset?: number) {
    const mayExist = identifiers.find(rs => rs.RunID === resultset.RunID && rs.Hidden === false);

    if (mayExist && mayExist.Order > 0) {
      return mayExist.Order;
    } else {
      return this.resultsStore.getNextOrder(identifiers, !resultset.RunID_Used ? 'root' : resultset.RunID_Used, offset);
    }
  }

  /**
   * Calls the Simulation Runner runSimulation() method using the simulationSetState
   */
  public runSimulation() {
    this.DBstate = 'running';
    const simulationsToRun = this.getSimulationTree();

    this.resultsService
      .getResultSetIdentifiers()
      .toPromise()
      .then(identifiers => {
        const baselinesCanBeReordered = this.canBeReordered('root', simulationsToRun);
        let baselinesWithNoOrdering = 0;

        for (let [index, baseline] of simulationsToRun.entries()) {
          baseline.Order = baselinesCanBeReordered
            ? index + 1
            : this.getCurrentOrderOrNew(identifiers, baseline, ++baselinesWithNoOrdering);

          const stressCanBeReordered = this.canBeReordered(baseline.RunID, baseline.StressList);
          let stressWithNoOrdering = 0;
          for (let [stressIndex, stress] of baseline.StressList.entries()) {
            stress.Order = stressCanBeReordered ? stressIndex + 1 : this.getCurrentOrderOrNew(identifiers, stress, ++stressWithNoOrdering);
          }
        }

        this.simRunner.rerunId = null;
        this.simRunner.runSimulation(simulationsToRun);
        this.resultsService.clearResultSetIdentifiers(); // This will force a new updated fetch next time we need identifiers
      });
    this.DBstate = this.currentResultSet && this.DBstate === 'running' ? 'completed' : 'running';
  }

  /**
   * Finds a previous run of the combination of Scenario and Dataset
   * For stresses, it has to match the parent Scenario and Dataset as well
   */
  public findResultsetItemInDB(
    baselineScenarioId: number,
    datasetId: number,
    stressScenarioId?: number,
    stressDatasetId?: number
  ): ResultsetItem {
    let found = null;
    const dbResultSets = this.resultsService.resultSetIdentifiers$.getValue();
    if (dbResultSets && stressScenarioId) {
      // Find associated baseline
      const baseline = dbResultSets.find(resultset => resultset.Scenario_ID === baselineScenarioId && resultset.DataSetID === datasetId);
      // Find stress
      const stresses = dbResultSets.filter(
        resultset => resultset.Scenario_ID === stressScenarioId && resultset.DataSetID === stressDatasetId
      );

      // sort by temps first, then by date
      found = stresses
        .sort((s, s2) => (s.IsTemp ? 1 : -1) - (s2.IsTemp ? 1 : -1) || 
          (s2.Run_Date && s.Run_Date ? +new Date(s2.Run_Date) - +new Date(s.Run_Date) : 0))
        .find(resultset => resultset.RunID_Used === (baseline ? baseline.RunID : ''));
    } else {
      // Find baseline, sort by temps first, then by date
      found = dbResultSets
        .sort((s, s2) => (s.IsTemp ? 1 : -1) - (s2.IsTemp ? 1 : -1) || 
          (s2.Run_Date && s.Run_Date ? +new Date(s2.Run_Date) - +new Date(s.Run_Date) : 0))
        .find(resultset => resultset.Scenario_ID === baselineScenarioId && resultset.DataSetID === datasetId);
    }
    return found || null;
  }
}
